const escapeHtml = (value = '') => String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const inline = (value = '') => {
  let text = escapeHtml(String(value).trim());
  text = text.replace(/\[([^\]]+)\]\(([^\)]+)\)/g, '<a href="$2">$1</a>');
  text = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  text = text.replace(/__([^_]+)__/g, '<strong>$1</strong>');
  text = text.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>');
  return text;
};

const sentenceGroups = (text = '', target = 430) => {
  const sentences = String(text).replace(/\s+/g, ' ').trim().match(/[^.!?]+(?:[.!?]+(?=\s|$)|$)/g) || [];
  const groups = [];
  let current = '';
  sentences.forEach((raw) => {
    const sentence = raw.trim();
    if (!sentence) return;
    if (current && current.length + sentence.length + 1 > target) { groups.push(current); current = sentence; }
    else current = current ? current + ' ' + sentence : sentence;
  });
  if (current) groups.push(current);
  return groups;
};

const textOnly = (value = '') => String(value).replace(/<[^>]*>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/&amp;/gi, '&').replace(/\s+/g, ' ').trim();

const canonicalHref = (href = '') => {
  const value = String(href);
  const match = value.match(/^https?:\/\/(?:www\.)?letsgowanderbout\.com\/(?:blogposts?|post)\/([^?#]+)/i);
  if (match) return '/wanders/' + match[1].replace(/\/$/, '');
  const product = value.match(/^https?:\/\/(?:www\.)?letsgowanderbout\.com\/product-page\/([^?#]+)/i);
  if (product) return '/found/' + product[1].replace(/\/$/, '');
  const fieldNote = value.match(/^https?:\/\/(?:www\.)?letsgowanderbout\.com\/field-notes?\/([^?#]+)/i);
  if (fieldNote) return '/field-notes/' + fieldNote[1].replace(/\/$/, '');
  if (/^https?:\/\/(?:www\.)?letsgowanderbout\.com\/(?:contact(?:-\d+)?|about)\/?$/i.test(value)) return '/about';
  if (/^https?:\/\/(?:www\.)?letsgowanderbout\.com(?:\/(?:book-online|blank-1))?\/?$/i.test(value)) return '/';
  return value;
};

export function normalizeRichTextHtml(value = '') {
  let html = String(value || '').trim();
  if (!html) return '';
  // Affiliate images belong to the offer record for card layouts. Keep editorial links
  // inside the article as ordinary text, including older rich-text inserts.
  html = html.replace(/<p\b([^>]*\bclass=["'][^"']*\baffiliate-inline\b[^"']*["'][^>]*)>([\s\S]*?)<\/p>/gi, (_, attrs, body) => {
    const links = body.replace(/<a\b([^>]*)>([\s\S]*?)<\/a>/gi, (_anchor, linkAttrs, label) =>
      '<a' + linkAttrs.replace(/\s+style=["'][^"']*["']/gi, '') + '>' + label.replace(/<img\b[^>]*\/?>/gi, '').replace(/<\/?span\b[^>]*>/gi, '') + '</a>'
    );
    return '<p' + attrs + '>' + links + '</p>';
  });
  html = html.replace(/href=(["'])(.*?)\1/gi, (_, quote, href) => 'href=' + quote + canonicalHref(href) + quote);
  html = html.replace(/(?:^|\s)(?:https?:\/\/)?(?:www\.)?letsgowanderbout\.com\/(?:product-page|blogposts?|post)\/[^\s<)]+/gi, '');
  html = html.replace(/(?:^|\s)com\/(?:product-page|contact[^\s<)]*|blog[^\s<)]*)/gi, '');

  html = html.replace(/<h([1-6])\b[^>]*>([\s\S]*?)<\/h\1>/gi, (whole, level, inner) => {
    const text = textOnly(inner);
    const hasList = /\s+-\s+/.test(text);
    if (text.length <= 140 && !hasList && !/!\[|https?:\/\//i.test(text)) return whole;
    const parts = text.split(/\s+-\s+/).map((part) => part.trim()).filter(Boolean);
    let lead = parts.shift() || text;
    let title = '';
    let remainder = '';
    const boundaries = [...lead.matchAll(/[.!?:](?=\s+[A-Z])/g)].map((match) => match.index + 1).filter((index) => index >= 12 && index <= 110);
    if (boundaries.length) {
      const boundary = boundaries[0];
      title = lead.slice(0, boundary).trim();
      remainder = lead.slice(boundary).trim();
    } else if (lead.length <= 140) {
      title = lead;
    } else {
      remainder = lead;
    }
    const heading = title ? '<h' + level + '>' + inline(title) + '</h' + level + '>' : '';
    const paragraph = remainder ? '<p>' + inline(remainder) + '</p>' : '';
    const list = parts.length ? '<ul>' + parts.map((part) => '<li>' + inline(part) + '</li>').join('') + '</ul>' : '';
    return heading + paragraph + list;
  });
  return html.replace(/<p>\s*<\/p>/gi, '').replace(/\n{3,}/g, '\n\n').trim();
}

export function richTextHtml(value = '') {
  const source = String(value || '').trim();
  if (!source) return '';
  if (/<([a-z][\w-]*)(?:\s[^>]*)?>/i.test(source) && !/<(?:p|div)[^>]*>\s*#{2,4}\s+/i.test(source)) return normalizeRichTextHtml(source);

  const normalized = source
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(?:p|div|h[1-6]|li|blockquote)>/gi, '\n\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ').replace(/&amp;/gi, '&')
    .replace(/\s+(#{2,4}\s+)/g, '\n\n$1')
    .replace(/([^\n])\s+([-*]\s+)/g, '$1\n$2')
    .replace(/\r/g, '').replace(/\n{3,}/g, '\n\n').trim();

  const rendered = normalized.split(/\n{2,}/).map((block) => block.trim()).filter(Boolean).flatMap((block) => {
    const heading = block.match(/^(#{2,4})\s+([\s\S]+)$/);
    if (heading) {
      const level = Math.min(4, heading[1].length);
      return ['<h' + level + '>' + inline(heading[2].replace(/\n+/g, ' ')) + '</h' + level + '>'];
    }
    const lines = block.split('\n').map((line) => line.trim()).filter(Boolean);
    if (lines.length && lines.every((line) => /^[-*]\s+/.test(line))) {
      return ['<ul>' + lines.map((line) => '<li>' + inline(line.replace(/^[-*]\s+/, '')) + '</li>').join('') + '</ul>'];
    }
    if (lines.length && lines.every((line) => /^\d+[.)]\s+/.test(line))) {
      return ['<ol>' + lines.map((line) => '<li>' + inline(line.replace(/^\d+[.)]\s+/, '')) + '</li>').join('') + '</ol>'];
    }
    const joined = lines.join(' ');
    return (joined.length > 900 ? sentenceGroups(joined) : [joined]).filter(Boolean).map((paragraph) => '<p>' + inline(paragraph) + '</p>');
  }).join('\n');
  return normalizeRichTextHtml(rendered);
}
